import { AlertTriangle } from 'lucide-react'
import { UploadDepositReceiptForm } from './UploadDepositReceiptForm'
import type { Donation } from '@/features/donations/types/donation'
import { Badge } from '@/components/custom/badge'
import { Button } from '@/components/ui/button'
import { formatCurrency } from '@/utils/currencyUtils'
import { formatDate } from '@/utils/timeZoneUtils'
import ExportDialog from '@/features/receipts/components/ExportDialog'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { usePermissions } from '@/utils/permissions'

interface DonationCardProps {
  donation: Donation
  type: 'pending' | 'unsent' | 'notDeposited'
  donorName: string
  onVerifyClick?: (donationId: string) => void
  onUploadClick?: (donationId: string) => void
}

export function DonationCard({
  donation,
  type,
  donorName,
  onVerifyClick,
  onUploadClick,
}: DonationCardProps) {
  const churches = useChurchesStore((s) => s.churches)
  const permissions = usePermissions()
  const isPending = type === 'pending'
  const isUnsent = type === 'unsent'
  const isNotDeposited = type === 'notDeposited'
  const needsManualAssignment = !donation.churchId || !donation.donorId

  // Determine if user can verify this specific donation
  const canVerifyThisDonation = () => {
    // Super admin can always verify
    if (permissions.isSystemAdmin()) return true

    // If donation has no churchId, only super admin can verify (already handled above)
    if (!donation.churchId) return false

    // If donation has churchId, treasurer from that church can verify
    if (permissions.isTreasurer()) {
      return permissions.canVerifyDonation(donation.churchId)
    }

    return false
  }

  const church = churches.find((c) => c._id === donation.churchId)
  const churchName = church ? church.name : 'Unassigned Church'

  return (
    <div
      className={`border rounded-lg p-4 hover:shadow-md transition-shadow min-w-70 ${
        isPending && needsManualAssignment ? 'border-amber-200 bg-amber-50' : ''
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="font-semibold text-lg">
            {formatCurrency(donation.amount)}
          </div>
          {isPending && needsManualAssignment && (
            <AlertTriangle className="h-4 w-4 text-amber-600" />
          )}
        </div>
        <Badge color="yellow" size="sm">
          {isPending ? 'Pending' : isUnsent ? 'Unsent' : 'Not Deposited'}
        </Badge>
      </div>

      <div className="space-y-1 mb-4">
        <div className="text-sm font-medium text-gray-900">{donorName}</div>
        <div className="text-xs text-gray-500">{churchName}</div>
        <div className="text-xs text-gray-500">
          Receipt # {donation.receiptNumber}
        </div>
        <div className="text-xs text-gray-500">
          {formatDate(donation.date, 'MMM D, YYYY')}
        </div>
        {isPending && needsManualAssignment && (
          <div className="text-xs text-amber-700 font-medium">
            Manual assignment required
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="space-y-2">
        {isPending && canVerifyThisDonation() && (
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => onVerifyClick?.(donation._id)}
          >
            Verify Donation
          </Button>
        )}

        {donation.status === 'verified' &&
          donation.donorId &&
          !donation.isRegistrationFee && (
            <ExportDialog
              type="donation"
              memberId={donation.donorId}
              id={donation._id}
              trigger={
                <Button variant="outline" size="sm" className="w-full">
                  {`${isUnsent ? 'Send' : 'Resend'} Receipt`}
                </Button>
              }
            />
          )}
        {isNotDeposited && canVerifyThisDonation() && (
          <UploadDepositReceiptForm
            donationId={donation._id}
            trigger={
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => onUploadClick?.(donation._id)}
              >
                Upload Receipt
              </Button>
            }
          />
        )}
      </div>
    </div>
  )
}
